/**
 * Water-column query — the one deterministic "is there water here, how deep, and
 * which way does it flow" helper, shared by client physics (wade/swim/buoyancy)
 * and server AI (water avoidance / wading). Pure function of the seed-derived
 * sampler + planet def, so client and server agree without syncing the surface.
 *
 * All inputs are PLANET-LOCAL (the unwrapped sphere direction, same space as
 * `PlanetSampler.sample`). Callers add `submersion = seaRadius − r` (how far a
 * point at radius r sits below the flat sea level) and rotate the flow tangent
 * into world space by the planet's spin when they need a current vector.
 */
import type { PlanetSampler, PlanetDef } from "./universe.js";
import { createSurfaceColumn, surfaceColumn } from './surface.js';

export interface WaterColumn {
  /** Solid support beneath water (metres from centre); includes grounded ice.
   * Use surfaceColumn.bedR for the rock bed below grounded ice. */
  floorR: number;
  /** seaRadius − floorR on WATER seas. −Inf-ish on dry, ice or lava basins. */
  floorDepth: number;
  /** The LOCAL water-surface radius (metres from centre): the flat sea level
   *  OR the river surface `ws` when a channel runs here, whichever is higher.
   *  −1e9 when neither exists. This is what made inland rivers swimmable —
   *  every wade/swim/float decision keys on it, never on seaRadius alone. */
  surfR: number;
  /** surfR − floorR: metres of actual water over the floor HERE (≤0 ⇒ dry).
   *  Unlike floorDepth this is river-aware. */
  surfDepth: number;
  /** Carved-channel wet strength 0..1 (rivers + lake beds). */
  riverK: number;
  /** Local river current speed, m/s (0 off a river / across settled ground). */
  flowSpeed: number;
  /** Downstream flow tangent (planet-local unit), 0 off a river. */
  fx: number;
  fy: number;
  fz: number;
}

const SCRATCH = createSurfaceColumn();

/** Fill `out` with the water column at a planet-local unit direction. */
export function waterColumn(
  sampler: PlanetSampler,
  def: PlanetDef,
  dx: number,
  dy: number,
  dz: number,
  out: WaterColumn,
): WaterColumn {
  surfaceColumn(sampler, def, dx, dy, dz, SCRATCH);
  out.floorR = SCRATCH.supportR;
  out.floorDepth = SCRATCH.seaWaterDepth;
  out.surfR = SCRATCH.waterR;
  out.surfDepth = SCRATCH.waterDepth;
  out.riverK = SCRATCH.riverK;
  out.flowSpeed = SCRATCH.flowSpeed;
  out.fx = SCRATCH.fx;
  out.fy = SCRATCH.fy;
  out.fz = SCRATCH.fz;
  return out;
}
